import { compileRoomBoundaryPatch, type CompiledCreation, type CreationDraft,
  type ShapeBlob, type ShapeOpening } from '../../../../ft3d/engine/ft3d-creation-core.js';
import { validateShape } from '../../../../ft3d/engine/ft3d-shape-core.js';

export type DoorOpeningMutation = { operation: 'move' | 'resize' | 'delete'; span?: ShapeOpening['span']; z?: ShapeOpening['z'] };
type CreationConnectionOverride = CreationDraft['connectionOverrides'][number];
export type DoorOpeningDecision = { opening: ShapeOpening | null; connectionOverride: CreationConnectionOverride | null };
const pairKey = (ids: readonly string[]): string => [...ids].sort().join('|').toUpperCase();
// PHP round() used half away from zero, including negative wall coordinates.
const halfFoot = (value: number): number => Math.sign(value) * Math.round(Math.abs(value) * 2) / 2;

/** The wall intersection and lowest ceiling belong to the authored door, independently of the view cut. */
export function doorOpeningWall(shape: ShapeBlob, opening: ShapeOpening) {
  const ids = opening.creation?.rooms?.filter(id => typeof id === 'string' && id !== '') ?? [];
  const rooms = shape.boxes.filter(box => {
    const face = box[opening.plane.axis];
    return (!ids.length || ids.includes(box.id))
      && (Math.abs(opening.plane.at - face.min) <= 1e-6 || Math.abs(opening.plane.at - face.max) <= 1e-6);
  });
  const along = opening.plane.axis === 'x' ? 'y' : 'x';
  const min = Math.max(...rooms.map(box => box[along].min)), max = Math.min(...rooms.map(box => box[along].max));
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) throw new Error('door-edit:wall-unresolved');
  return { min, max, ceiling: Math.min(...rooms.map(box => box.ceiling)), roomIds: rooms.map(box => box.id) };
}

/** Apply one already-decided row; all unrelated authored objects retain their identity. */
export function applyDoorOpeningDecision(shape: ShapeBlob, openingId: string, decision: DoorOpeningDecision): ShapeBlob {
  const index = shape.openings.findIndex(opening => opening.id === openingId);
  if (index < 0) throw new Error('door-edit:opening-not-found');
  const openings = shape.openings.slice();
  openings.splice(index, 1, ...(decision.opening ? [decision.opening] : []));
  let authoring = shape.authoring;
  if (decision.connectionOverride) {
    const key = pairKey(decision.connectionOverride.roomIds);
    const rows = Array.isArray(authoring?.connectionOverrides) ? authoring.connectionOverrides as CreationConnectionOverride[] : [];
    const overrides = rows.filter(row => !Array.isArray(row?.roomIds) || pairKey(row.roomIds) !== key);
    overrides.push(decision.connectionOverride);
    authoring = { ...authoring, connectionOverrides: overrides };
  }
  return { ...shape, openings, ...(authoring !== shape.authoring ? { authoring } : {}) };
}

export function planDoorOpeningEdit(shape: ShapeBlob, openingId: string, mutation: DoorOpeningMutation) {
  const stored = shape.openings.find(opening => opening.id === openingId);
  if (stored?.creation?.connectionIntent?.kind !== 'door-intent') throw new Error('door-edit:opening-not-found');
  const wall = doorOpeningWall(shape, stored);
  let opening: ShapeOpening | null = null;
  if (mutation.operation !== 'delete') {
    if (!mutation.span || !mutation.z || ![mutation.span.min, mutation.span.max, mutation.z.min, mutation.z.max].every(Number.isFinite))
      throw new Error('door-edit:finite-ranges-required');
    const span = { min: halfFoot(mutation.span.min), max: halfFoot(mutation.span.max) };
    const z = { min: halfFoot(mutation.z.min), max: halfFoot(mutation.z.max) };
    const width = span.max - span.min, height = z.max - z.min;
    if (width < 2.5 || width > 20 || height < 5 || height > 20 || Math.abs(z.min - stored.z.min) > 1e-6
      || span.min < wall.min - 1e-6 || span.max > wall.max + 1e-6 || z.max > wall.ceiling + 1e-6)
      throw new Error('door-edit:wall-or-floor-bounds');
    opening = { ...stored, span, z, creation: { ...stored.creation, automatic: false } };
  }
  const ids = stored.creation.rooms?.filter(id => typeof id === 'string' && id !== '') ?? [];
  const connectionOverride: CreationConnectionOverride | null = ids.length === 2 ? {
    roomIds: [ids[0]!, ids[1]!], mode: 'wall', doorItemId: null, doorMeshId: null, doorWidthFt: null, queryOnly: true,
  } : null;
  const decision = { opening, connectionOverride };
  const next = applyDoorOpeningDecision(shape, openingId, decision);
  try { validateShape(next); } catch { throw new Error('door-edit:shape-failed-validation'); }
  return { ...decision, shape: next, roomIds: wall.roomIds };
}

/** Recompile just the owning wall faces. Floors, cells, dressing and unrelated boundaries stay resident. */
export function projectDoorOpeningEdit(previous: CompiledCreation, held: CreationDraft, openingId: string,
  decision: DoorOpeningDecision): CompiledCreation {
  const original = previous.shape.openings.find(opening => opening.id === openingId);
  if (!original) return previous; // duplicate delete echo
  const wall = doorOpeningWall(previous.shape, original);
  const shape = applyDoorOpeningDecision(previous.shape, openingId, decision);
  const authoringShape = previous.authoringShape && previous.authoringShape !== previous.shape
    ? applyDoorOpeningDecision(previous.authoringShape, openingId, decision) : shape;
  const manual = held.manualOpenings.filter(opening => opening.id !== openingId);
  if (decision.opening) manual.push(decision.opening);
  // The boundary compiler consumes normalized pair order; saved metadata keeps its authored order.
  const override = decision.connectionOverride && { ...decision.connectionOverride,
    roomIds: [...decision.connectionOverride.roomIds].sort() as [string, string] };
  const overrides = override
    ? [...held.connectionOverrides.filter(row => pairKey(row.roomIds) !== pairKey(override.roomIds)), override]
    : held.connectionOverrides;
  const draft = { ...held, manualOpenings: manual, connectionOverrides: overrides };
  const affected = new Set(wall.roomIds);
  const patch = compileRoomBoundaryPatch(draft, shape, affected, previous.solution);
  const retain = <T extends { roomIds: string[] }>(before: readonly T[], after: readonly T[]): T[] => {
    const prior = new Map(before.filter(row => row.roomIds.some(id => affected.has(id))).map(row => [JSON.stringify(row), row]));
    return [...before.filter(row => !row.roomIds.some(id => affected.has(id))), ...after.map(row => prior.get(JSON.stringify(row)) ?? row)];
  };
  const roomGroup = { ...previous.roomGroup, wallRuns: retain(previous.roomGroup.wallRuns, patch.wallRuns),
    connectionIntents: retain(previous.roomGroup.connectionIntents, patch.connectionIntents),
    sharedBoundaries: retain(previous.roomGroup.sharedBoundaries, patch.sharedBoundaries) };
  return { ...previous, draft, shape, authoringShape, roomGroup,
    summary: { ...previous.summary, wallRunCount: roomGroup.wallRuns.length, connectionIntentCount: roomGroup.connectionIntents.length } };
}
